import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Copy, Check, ShieldCheck, FileText, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Donate() {
    const [content, setContent] = useState({});
    const [settings, setSettings] = useState({});
    const [documents, setDocuments] = useState([]);
    const [copied, setCopied] = useState("");
    const [form, setForm] = useState({ name: "", email: "", phone: "", amount: "", pan: "", address: "", message: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/site-content").then((r) => setContent(r.data || {}));
        api.get("/settings").then((r) => setSettings(r.data || {}));
        api.get("/documents").then((r) => setDocuments((r.data || []).slice(0, 4)));
    }, []);

    const copy = (label, value) => {
        navigator.clipboard.writeText(value);
        setCopied(label);
        setTimeout(() => setCopied(""), 2000);
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/enquiries/donation", {
                ...form,
                amount: form.amount ? parseFloat(form.amount) : null,
            });
            toast.success("Thank you! We'll send your 80G receipt shortly.");
            setForm({ name: "", email: "", phone: "", amount: "", pan: "", address: "", message: "" });
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally { setBusy(false); }
    };

    const amounts = content.donate_amounts || [500, 1000, 2500, 5000, 10000, 25000];

    return (
        <div data-testid="donate-page">
            <section className="pt-20 pb-12 md:pt-32 md:pb-16">
                <div className="container-hero grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-7">
                        <div className="label-eyebrow text-brand-forest mb-4">Donate</div>
                        <h1 className="font-display text-5xl sm:text-6xl font-semibold leading-[1.05]">Your gift becomes a child's school year.</h1>
                        <p className="text-slate-600 text-lg mt-6 leading-relaxed">{content.donate_appeal}</p>
                    </div>
                    <div className="md:col-span-5 bg-brand-forest text-white rounded-2xl p-8">
                        <div className="flex items-center gap-2 label-eyebrow text-brand-ochre mb-3"><ShieldCheck size={16} /> Tax Deductible</div>
                        <p className="text-white/85 text-sm leading-relaxed">All donations to Kabir Foundation are 80G-compliant. Share your PAN with the details below to receive a tax-deduction receipt.</p>
                    </div>
                </div>
            </section>

            {/* Suggested amounts */}
            <section className="pb-16">
                <div className="container-hero">
                    <div className="label-eyebrow text-brand-forest mb-4">Suggested Contributions</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {amounts.map((a) => (
                            <button
                                key={a}
                                type="button"
                                onClick={() => setForm((f) => ({ ...f, amount: String(a) }))}
                                data-testid={`amount-${a}`}
                                className={`py-5 rounded-xl border transition-colors duration-200 ${
                                    form.amount === String(a) ? "bg-brand-forest text-white border-brand-forest" : "bg-white text-brand-ink border-black/10 hover:border-brand-forest"
                                }`}
                            >
                                <div className="font-display text-2xl font-semibold">₹{a.toLocaleString("en-IN")}</div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Bank details + Form */}
            <section className="pb-24">
                <div className="container-hero grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-6 bg-white rounded-2xl border border-black/5 p-8">
                        <div className="flex items-center gap-2 mb-4"><Building2 size={20} className="text-brand-forest" strokeWidth={1.5} /><h2 className="font-display text-2xl font-medium">Bank / UPI Details</h2></div>
                        <p className="text-sm text-slate-500 mb-4">{content.donate_instructions}</p>
                        <div className="divide-y divide-black/5">
                            {[
                                ["Account Name", settings.account_name],
                                ["Bank", settings.bank_name],
                                ["Account Number", settings.account_number],
                                ["IFSC Code", settings.ifsc_code],
                                ["Branch", settings.branch],
                                ["UPI ID", settings.upi_id],
                            ].map(([k, v]) => v ? (
                                <div key={k} className="flex items-center justify-between py-3 gap-4">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{k}</div>
                                        <div className="text-brand-ink font-medium">{v}</div>
                                    </div>
                                    <button
                                        onClick={() => copy(k, v)}
                                        data-testid={`copy-${k.toLowerCase().replace(/\s/g, '-')}`}
                                        className="p-2 rounded-md hover:bg-brand-stone transition-colors duration-200"
                                        aria-label={`Copy ${k}`}
                                    >
                                        {copied === k ? <Check size={16} className="text-brand-forest" /> : <Copy size={16} className="text-slate-500" />}
                                    </button>
                                </div>
                            ) : null)}
                        </div>
                    </div>

                    <div className="md:col-span-6 bg-brand-stone rounded-2xl p-8">
                        <h2 className="font-display text-2xl font-medium mb-1">Send us your donation details</h2>
                        <p className="text-sm text-slate-500 mb-6">We'll email your 80G receipt within 48 hours.</p>
                        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="donation-form">
                            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="donation-name" placeholder="Full name *" className="col-span-1 sm:col-span-2 px-4 py-3 rounded-lg border border-black/10 bg-white text-sm" />
                            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="donation-email" placeholder="Email *" className="px-4 py-3 rounded-lg border border-black/10 bg-white text-sm" />
                            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="donation-phone" placeholder="Phone" className="px-4 py-3 rounded-lg border border-black/10 bg-white text-sm" />
                            <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="donation-amount" placeholder="Amount (₹)" className="px-4 py-3 rounded-lg border border-black/10 bg-white text-sm" />
                            <input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} data-testid="donation-pan" placeholder="PAN (for 80G)" className="px-4 py-3 rounded-lg border border-black/10 bg-white text-sm" />
                            <textarea rows="3" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="donation-address" placeholder="Address" className="col-span-1 sm:col-span-2 px-4 py-3 rounded-lg border border-black/10 bg-white text-sm resize-none" />
                            <textarea rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="donation-message" placeholder="Any note or dedication (optional)" className="col-span-1 sm:col-span-2 px-4 py-3 rounded-lg border border-black/10 bg-white text-sm resize-none" />
                            <button type="submit" disabled={busy} data-testid="donation-submit" className="col-span-1 sm:col-span-2 mt-2 px-6 py-3.5 rounded-full bg-brand-forest text-white font-semibold hover:bg-brand-forestHover disabled:opacity-50 transition-colors duration-200">
                                {busy ? "Submitting…" : "Share my donation details"}
                            </button>
                        </form>
                    </div>
                </div>
            </section>

            {/* Transparency preview */}
            <section className="pb-24">
                <div className="container-hero">
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                        <h2 className="font-display text-3xl sm:text-4xl font-medium">Transparency & accountability</h2>
                        <Link to="/transparency" className="text-sm font-semibold text-brand-forest inline-flex items-center gap-1">See all documents <ArrowRight size={14} /></Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {documents.map((d) => (
                            <div key={d.id} className="bg-white rounded-xl p-6 border border-black/5">
                                <FileText size={22} className="text-brand-forest mb-3" strokeWidth={1.5} />
                                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{d.category}</div>
                                <div className="font-display text-lg mt-1">{d.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
